<script setup>
import { ref, onMounted, computed, watch, onBeforeUnmount } from "vue"
import { useRoute, useRouter } from "vue-router"
import iot from "@/api/iot"
import { getWaterStatistics, listWaterStations } from "@/api/waterLogger"

const route = useRoute()
const router = useRouter()
import HighchartsWrapper from "@/components/Charts/HighchartsWrapper.vue"

// state
const devices = ref([])
const selectedDevice = ref(null)
const loading = ref(false)
const error = ref("")
const waterStation = ref(null)
const waterStats = ref(null)

// Shared date range across all widgets
const fromDate = ref(new Date(Date.now() - 7 * 24 * 3600 * 1000))
const toDate = ref(new Date())

// Widgets loaded from database
const widgets = ref([])
// Chart instances map (keyed by widgetId)
const chartInstances = ref(new Map())

function fmtInput(dt) {
  const pad = (n) => String(n).padStart(2, "0")
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`
}

function parseInput(v) { 
  const date = new Date(v)
  // Set to start of day for proper date handling
  date.setHours(0, 0, 0, 0)
  return date
}

function formatDateDisplay(dt) {
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(dt.getDate())}/${pad(dt.getMonth()+1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`
}

async function loadDevices() {
  error.value = ""
  try {
    const list = await iot.getDevices()
    const normalized = (list || []).map(d => ({
      deviceId: d.deviceId || d.DeviceId || '',
      deviceName: d.deviceName || d.DeviceName || '',
      deviceType: d.deviceType || d.DeviceType || '',
      deviceRowId: d.deviceRowId || d.DeviceRowId || 0
    }))
    devices.value = normalized
    const deviceIdFromRoute = route.query?.deviceId
    const deviceRowIdFromRoute = route.query?.deviceRowId
    const match = deviceIdFromRoute || deviceRowIdFromRoute
      ? normalized.find(d =>
          (deviceIdFromRoute && (d.deviceId === deviceIdFromRoute || String(d.deviceId) === String(deviceIdFromRoute))) ||
          (deviceRowIdFromRoute && (d.deviceRowId === Number(deviceRowIdFromRoute) || String(d.deviceRowId) === String(deviceRowIdFromRoute)))
        )
      : null
    if (match) {
      selectedDevice.value = match
      await loadWidgets()
    } else if (!selectedDevice.value && normalized.length) {
      selectedDevice.value = normalized[0]
      await loadWidgets()
    }
  } catch (e) {
    error.value = "Failed to load devices"
  }
}

function mapTypeToQueryType(type) {
  // Type: 1 = cumulative/get, 2 = dow, 3 = daily
  switch (type) {
    case 1: return 'cumulative'
    case 2: return 'dow'
    case 3: return 'daily'
    default: return 'cumulative'
  }
}

async function loadWidgets() {
  if (!selectedDevice.value) return
  
  error.value = ""
  loading.value = true
  try {
    waterStation.value = null
    waterStats.value = null
    if (isWaterDevice(selectedDevice.value)) {
      const stations = await listWaterStations()
      waterStation.value = stations.find(s => s.externalStationId === selectedDevice.value.deviceId) || null
      widgets.value = [{
        widgetId: `water-${selectedDevice.value.deviceId}`,
        type: 'water-level',
        title: 'Water-table depth',
        points: [], slices: [], total: null, battery: null, voltage: null
      }]
      await loadWidgetData(widgets.value[0])
      return
    }
    const widgetList = await iot.getWidgets(selectedDevice.value.deviceId)
    widgets.value = (widgetList || []).map(w => ({
      widgetId: w.widgetId || w.WidgetId,
      type: mapTypeToQueryType(w.type || w.Type),
      title: w.title || w.Title || 'Untitled Widget',
      dataJson: w.dataJson || w.DataJson,
      sortOrder: w.sortOrder || w.SortOrder || 0,
      points: [],
      slices: [],
      total: 0,
      battery: null,
      voltage: null
    }))
    
    // Load data for each widget
    await Promise.all(widgets.value.map(w => loadWidgetData(w)))
  } catch (e) {
    error.value = "Failed to load widgets"
    console.error("Widget load error:", e)
    // Fallback to empty widgets if API fails
    widgets.value = []
  } finally {
    loading.value = false
  }
}

function getDeviceType(device) {
  if (!device) return "DING"
  const type = (device.deviceType || "").toLowerCase()
  // PIP family — PeopleSense devices, or DeviceTypeId "1"
  if (type.includes("peoplesense") || type.includes("pip") || type === "1") {
    return "PIP"
  }
  // Eco-Counter devices use the same per-bucket shape as PIP (one reading per day,
  // not cumulative). Route through the PIP server-side path so daily/DOW aggregations work.
  if (type.includes("eco-counter") || type.includes("ecocounter")) {
    return "PIP"
  }
  if (isWaterDevice(device)) return "WATER_LOGGER"
  // Default to DING (cumulative) for everything else
  return "DING"
}

function isWaterDevice(device) {
  const type = (device?.deviceType || "").toLowerCase()
  return type.includes("water logger") || type.includes("orpheus") || type.includes("levelogger")
}

function openWaterStationDetails() {
  router.push({ name: "WaterLogger", query: { deviceId: selectedDevice.value?.deviceId } })
}

async function loadWidgetData(widget) {
  if (!selectedDevice.value) return

  try {
    const deviceType = getDeviceType(selectedDevice.value)

    const from = new Date(fromDate.value)
    from.setHours(0, 0, 0, 0)

    const to = new Date(toDate.value)
    to.setHours(23, 59, 59, 999)

    const base = {
      deviceId: selectedDevice.value.deviceId,
      deviceType: deviceType,
      fromUtcMs: from.getTime(),
      toUtcMs: to.getTime(),
      customerId: "" // Will be resolved on backend from user context
    }

    if (widget.type === 'cumulative') {
      const response = await iot.getSeries(base)
      const series = response.data || response || []
      // Backend returns per-event increments for PIP; running sum here for the cumulative line.
      let running = 0
      widget.points = series.map(p => {
        running += Number(p.value) || 0
        return { ts: p.ts, value: running, voltage: p.voltage }
      })
      widget.total = widget.points.length > 0 ? Math.round(widget.points[widget.points.length - 1].value) : 0
      // Get latest battery/voltage from the data
      const latest = widget.points.filter(p => p.voltage != null).slice(-1)[0]
      if (latest) {
        widget.voltage = latest.voltage
        // Estimate battery percentage from voltage (rough calculation for 3.0-4.5V range)
        widget.battery = Math.round(Math.max(0, Math.min(100, ((latest.voltage - 3.0) / 1.5) * 100)))
      }
    } else if (widget.type === 'water-level') {
      const response = await iot.getSeries(base)
      widget.points = (response.data || response || []).map(p => ({ ts: p.ts, value: Number(p.value), voltage: p.voltage }))
      widget.total = widget.points.length ? widget.points[widget.points.length - 1].value : null
      if (waterStation.value) {
        waterStats.value = await getWaterStatistics(waterStation.value.id, {
          from: from.toISOString(), to: to.toISOString()
        })
      }
    } else if (widget.type === 'dow') {
      const response = await iot.getDOW(base)
      widget.slices = response.data || response || []
      widget.total = widget.slices.reduce((sum, s) => sum + (s.total || 0), 0)
      // Get battery/voltage from cumulative widget if available
      const cumulativeWidget = widgets.value.find(w => w.type === 'cumulative')
      if (cumulativeWidget?.points?.length > 0) {
        const latest = cumulativeWidget.points.filter(p => p.voltage != null).slice(-1)[0]
        if (latest) {
          widget.voltage = latest.voltage
          widget.battery = Math.round(Math.max(0, Math.min(100, ((latest.voltage - 3.0) / 1.5) * 100)))
        }
      }
    } else if (widget.type === 'daily') {
      const response = await iot.getDaily(base)
      widget.points = (response.data || response || []).map(d => ({ ts: d.dayStartTs, total: d.total }))
      widget.total = widget.points.reduce((sum, p) => sum + (p.total || 0), 0)
      // Get battery/voltage from cumulative widget if available
      const cumulativeWidget = widgets.value.find(w => w.type === 'cumulative')
      if (cumulativeWidget?.points?.length > 0) {
        const latest = cumulativeWidget.points.filter(p => p.voltage != null).slice(-1)[0]
        if (latest) {
          widget.voltage = latest.voltage
          widget.battery = Math.round(Math.max(0, Math.min(100, ((latest.voltage - 3.0) / 1.5) * 100)))
        }
      }
    }
  } catch (e) {
    // Try to parse error response - could be JSON or text
    let errorData = {}
    if (e.response) {
      const contentType = e.response.headers?.['content-type'] || ''
      if (contentType.includes('application/json')) {
        errorData = e.response.data || {}
      } else {
        try {
          errorData = typeof e.response.data === 'string' ? JSON.parse(e.response.data) : e.response.data
        } catch {
          errorData = { error: e.response.data, raw: true }
        }
      }
    }

    let errorMsg = errorData.error || errorData.message || e.message

    // Check for Cosmos DB container not found errors
    if (errorMsg && (errorMsg.includes("does not exist") || errorMsg.includes("NotFound") || errorMsg.includes("404"))) {
      errorMsg = `Cosmos DB container not found. The container for device type "${getDeviceType(selectedDevice.value)}" may not exist. Please verify the container exists in your Cosmos DB account.`
    }

    const paramInfo = errorData.paramName ? ` (Parameter: ${errorData.paramName})` : ''
    console.error(`Failed to load widget ${widget.widgetId} (${widget.type}): ${errorMsg}${paramInfo}`)
  }
}

// Highcharts loading function - prefer npm package, fallback to CDN
async function loadHighcharts() {
  if (typeof window.Highcharts !== "undefined") {
    return;
  }
  try {
    const Highcharts = (await import("highcharts")).default;
    window.Highcharts = Highcharts;
    return;
  } catch (_e) {
    // Fallback to CDN
  }
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[src*="highcharts.js"]');
    if (existingScript) {
      const checkInterval = setInterval(() => {
        if (typeof window.Highcharts !== "undefined") {
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);
      setTimeout(() => {
        clearInterval(checkInterval);
        if (typeof window.Highcharts === "undefined") {
          reject(new Error("Highcharts loading timeout"));
        } else {
          resolve();
        }
      }, 10000);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://code.highcharts.com/highcharts.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Highcharts. Check your network or try again."));
    document.head.appendChild(script);
  });
}

// Build Highcharts options for Line Chart (Card 1 - Cumulative)
function buildLineChartOptions(points, yLabel = "Count") {
  // Convert points from { ts, value } to [timestamp, value] format
  const seriesData = (points || []).map(p => [p.ts, p.value || 0]);
  
  return {
    chart: {
      type: 'line',
      height: 300,
      animation: false,
    },
    title: {
      text: null
    },
    xAxis: {
      type: 'datetime',
      title: {
        text: 'Time'
      }
    },
    yAxis: {
      title: {
        text: yLabel
      },
      allowDecimals: yLabel !== "Count"
    },
    legend: {
      enabled: false
    },
    tooltip: {
      shared: false,
      formatter: function() {
        return `<b>${new Date(this.x).toLocaleString()}</b><br/>${yLabel}: ${this.y}`;
      }
    },
    plotOptions: {
      line: {
        marker: {
          enabled: false,
          radius: 0
        },
        lineWidth: 1.5,
        enableMouseTracking: true
      },
      series: {
        animation: false
      }
    },
    series: [{
      name: yLabel,
      data: seriesData
    }],
    credits: {
      enabled: false
    }
  };
}

// Build Highcharts options for Pie Chart (Card 2 - Day of Week)
function buildPieChartOptions(slices) {
  // Convert slices from { day, total, percentage } to { name, y } format
  const seriesData = (slices || []).map(s => ({
    name: `${s.day} (${(s.percentage || 0).toFixed(1)}%)`,
    y: s.total || 0
  }));
  
  return {
    chart: {
      type: 'pie',
      height: 300,
      animation: false,
    },
    title: {
      text: null
    },
    tooltip: {
      pointFormat: '<b>{point.percentage:.1f}%</b><br/>Total: {point.y}'
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        cursor: 'pointer',
        dataLabels: {
          enabled: false
        },
        showInLegend: true
      },
      series: {
        animation: false
      }
    },
    legend: {
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical'
    },
    series: [{
      name: 'Count',
      data: seriesData
    }],
    credits: {
      enabled: false
    }
  };
}

// Build Highcharts options for Column Chart (Card 3 - Daily)
function buildColumnChartOptions(points) {
  // Convert points from { ts, total } to categories and data
  const categories = (points || []).map(p => {
    const date = new Date(p.ts);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
  });
  const seriesData = (points || []).map(p => p.total || 0);
  
  return {
    chart: {
      type: 'column',
      height: 300,
      animation: false,
    },
    title: {
      text: null
    },
    xAxis: {
      categories: categories,
      title: {
        text: 'Day'
      }
    },
    yAxis: {
      title: {
        text: 'Count'
      },
      allowDecimals: false,
      min: 0
    },
    legend: {
      enabled: false
    },
    tooltip: {
      formatter: function() {
        return `<b>${this.x}</b><br/>Count: ${this.y}`;
      }
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        groupPadding: 0.1,
        pointPadding: 0.05
      },
      series: {
        animation: false
      }
    },
    series: [{
      name: 'Daily Total',
      data: seriesData
    }],
    credits: {
      enabled: false
    }
  };
}

// Get chart options for a widget
function getChartOptions(widget) {
  if (widget.type === 'cumulative') {
    return buildLineChartOptions(widget.points, "Count");
  } else if (widget.type === 'water-level') {
    return buildLineChartOptions(widget.points, "Water-table depth (m)");
  } else if (widget.type === 'dow') {
    return buildPieChartOptions(widget.slices);
  } else if (widget.type === 'daily') {
    return buildColumnChartOptions(widget.points);
  }
  return {};
}

// Chart ready handler
function onChartReady(widgetId, chart) {
  chartInstances.value.set(widgetId, chart);
}

async function reloadAllWidgetData() {
  if (!widgets.value.length) return
  await Promise.all(widgets.value.map(w => loadWidgetData(w)))
}

watch(() => selectedDevice.value, () => {
  if (selectedDevice.value) {
    loadWidgets()
  }
})

watch(() => route.query, (query) => {
  const deviceId = query?.deviceId
  const deviceRowId = query?.deviceRowId
  if ((deviceId || deviceRowId) && devices.value.length) {
    const match = devices.value.find(d =>
      (deviceId && (d.deviceId === deviceId || String(d.deviceId) === String(deviceId))) ||
      (deviceRowId && (d.deviceRowId === Number(deviceRowId) || String(d.deviceRowId) === String(deviceRowId)))
    )
    if (match && selectedDevice.value?.deviceId !== match.deviceId) {
      selectedDevice.value = match
    }
  }
}, { immediate: false })

onMounted(async () => {
  try {
    await loadHighcharts();
  } catch (e) {
    error.value = e?.message || "Failed to load charts. Device data may still work.";
    console.warn("Highcharts load failed:", e);
  }
  await loadDevices();
})

onBeforeUnmount(() => {
  // HighchartsWrapper destroys its own charts on unmount; avoid double-destroy
  chartInstances.value.forEach((chart) => {
    if (chart && typeof chart.destroy === "function") {
      try {
        chart.destroy();
      } catch (e) {
        console.warn("Chart destroy failed", e);
      }
    }
  });
  chartInstances.value.clear();
})
</script>

<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card-title class="ww-page-header">
          <div class="ww-page-heading">
            <div class="ww-page-title">IoT Dashboard</div>
            <div class="ww-page-subtitle">Monitor and analyze your IoT device data.</div>
          </div>
          <div class="d-flex align-center" style="gap: 12px; flex-wrap: wrap">
            <v-select
              v-model="selectedDevice"
              :items="devices"
              item-title="deviceName"
              return-object
              label="Select Device"
              density="compact"
              hide-details
              style="max-width: 300px; min-width: 220px"
              @update:model-value="loadWidgets"
            >
              <template #selection="{ item }">
                {{ item.raw.deviceName || item.raw.deviceId }}
              </template>
              <template #item="{ props, item }">
                <v-list-item v-bind="props">
                  <template #title>
                    {{ item.raw.deviceName || item.raw.deviceId }}
                  </template>
                  <template #subtitle>
                    {{ item.raw.deviceType }}
                  </template>
                </v-list-item>
              </template>
            </v-select>
            <v-text-field
              :model-value="fmtInput(fromDate)"
              @update:model-value="fromDate = parseInput($event)"
              type="date"
              label="Start"
              density="compact"
              hide-details
              variant="outlined"
              style="max-width: 170px"
              @change="reloadAllWidgetData"
            />
            <v-text-field
              :model-value="fmtInput(toDate)"
              @update:model-value="toDate = parseInput($event)"
              type="date"
              label="End"
              density="compact"
              hide-details
              variant="outlined"
              style="max-width: 170px"
              @change="reloadAllWidgetData"
            />
            <v-btn
              v-if="isWaterDevice(selectedDevice)"
              color="primary"
              variant="outlined"
              prepend-icon="mdi-water-thermometer"
              @click="openWaterStationDetails"
            >
              Station details
            </v-btn>
          </div>
        </v-card-title>
      </v-col>
    </v-row>

    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      dismissible
      class="ma-4"
      @update:model-value="error = ''"
    >
      {{ error }}
    </v-alert>

    <v-row v-if="isWaterDevice(selectedDevice) && waterStats" dense class="px-3 mb-2">
      <v-col v-for="item in [
        { label: 'Current WTD', value: widgets[0]?.total == null ? '—' : `${Number(widgets[0].total).toFixed(3)} m` },
        { label: 'Mean WTD', value: waterStats.mean == null ? '—' : `${Number(waterStats.mean).toFixed(3)} m` },
        { label: 'Range', value: waterStats.minimum == null ? '—' : `${Number(waterStats.minimum).toFixed(3)} to ${Number(waterStats.maximum).toFixed(3)} m` },
        { label: 'Completeness', value: `${waterStats.completenessPercentage || 0}%` },
        { label: 'Valid readings', value: waterStats.validObservationCount || 0 },
        { label: 'Longest gap', value: `${Math.round(waterStats.longestMissingMinutes || 0)} min` }
      ]" :key="item.label" cols="6" md="2">
        <v-card variant="tonal" class="pa-3 h-100">
          <div class="text-caption text-medium-emphasis">{{ item.label }}</div>
          <div class="text-h6">{{ item.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="selectedDevice && widgets.length > 0" style="margin-top: 0px">
      <v-col 
        v-for="widget in widgets" 
        :key="widget.widgetId" 
        cols="12" 
        :md="widget.type === 'water-level' ? 12 : 4"
      >
        <v-card elevation="3" class="d-flex flex-column" style="height: 100%">
          <!-- Header -->
          <v-card-title class="pa-4" style="background-color: #f5f5f5; border-bottom: 1px solid #e0e0e0">
            <div class="d-flex justify-space-between align-center w-100">
              <div class="flex-grow-1">
                <div class="text-h6 font-weight-bold">{{ selectedDevice.deviceName || selectedDevice.deviceId }}</div>
                <div class="d-flex align-center mt-1" style="gap: 8px">
                  <v-chip
                    v-if="widget.battery !== null && widget.voltage !== null"
                    size="small"
                    variant="flat"
                    color="grey-lighten-1"
                  >
                    Batt:{{ widget.battery }}% vlt:{{ widget.voltage?.toFixed(2) }}
                  </v-chip>
                  <span class="text-h5 font-weight-bold">{{ selectedDevice.deviceId }}</span>
                </div>
                <div class="text-caption text-medium-emphasis mt-1">{{ widget.title }}</div>
              </div>
              <div class="d-flex align-center" style="gap: 4px">
                <v-btn icon="mdi-pencil" variant="text" size="small" />
                <v-btn icon="mdi-delete" variant="text" size="small" />
                <v-btn icon="mdi-dots-vertical" variant="text" size="small" />
              </div>
            </div>
          </v-card-title>

          <!-- Chart Area -->
          <v-card-text class="flex-grow-1" style="min-height: 300px; position: relative">
            <HighchartsWrapper
              :options="getChartOptions(widget)"
              height="300px"
              @chart-ready="(chart) => onChartReady(widget.widgetId, chart)"
            />
          </v-card-text>

          <!-- Footer -->
          <v-card-actions class="pa-3" style="background-color: #fafafa; border-top: 1px solid #e0e0e0">
            <div class="d-flex align-center w-100">
              <v-spacer />
              <span v-if="widget.type === 'water-level'" class="text-body-2 font-weight-medium">Latest: {{ widget.total == null ? '—' : `${Number(widget.total).toFixed(3)} m` }}</span>
              <span v-else class="text-body-2 font-weight-medium">Ttl:{{ widget.total }}</span>
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else-if="selectedDevice && widgets.length === 0">
      <v-col cols="12">
        <v-card>
          <v-card-text class="text-center py-8">
            <v-icon size="64" color="grey">mdi-widgets-outline</v-icon>
            <div class="text-h6 mt-4">No Widgets Configured</div>
            <div class="text-body-2 text-medium-emphasis">No dashboard widgets found for this device. Please configure widgets in the database.</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12">
        <v-card>
          <v-card-text class="text-center py-8">
            <v-icon size="64" color="grey">mdi-radar</v-icon>
            <div class="text-h6 mt-4">No Device Selected</div>
            <div class="text-body-2 text-medium-emphasis">Please select a device to view dashboard data</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
